/* /account/reminders - reminders about deadlines, offer replies, missing documents and decisions, by email and on
   WhatsApp. Email is on for every student (the server turns it on; they can switch it off here); WhatsApp is an opt-in,
   never ticked for the student, and the number is confirmed with a code first. The page sends the browser's time zone
   and its language, which the email the server turned on follows. */
import { useState, type FormEvent } from 'react';
import {
  WEB_ROUTES, type ReminderContact, type ReminderKind, type ReminderSettings, type RemindersResponse,
} from '@pathabroad/core/browser';
import { useT } from '../app/i18n';
import { Link } from '../app/nav';
import { useMe } from '../app/session';
import { useErrorHandler, useLoad } from '../app/hooks';
import { Icon } from '../app/icons';
import { Alert, Button, Checkbox, Chip, CodeField, Live, LoadError, Loading, PageHeader, Row, Section, TextField } from '../app/ui';
import { api, ApiError } from '../lib/api';
import { formatDate, formatDateTime } from '../lib/format';
import { sixDigits } from '../lib/forms';
import type { MessageKey } from '../i18n/en';

const browserZone = () => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; } };
const KINDS: Array<{ key: keyof ReminderSettings; title: MessageKey; desc: MessageKey }> = [
  { key: 'deadlines', title: 'rem.kindDeadlines', desc: 'rem.kindDeadlinesDesc' },
  { key: 'offers', title: 'rem.kindOffers', desc: 'rem.kindOffersDesc' },
  { key: 'documents', title: 'rem.kindDocuments', desc: 'rem.kindDocumentsDesc' },
  { key: 'decisions', title: 'rem.kindDecisions', desc: 'rem.kindDecisionsDesc' },
];

export function Reminders() {
  const { t, lang } = useT();
  const data = useLoad(s => api.get<RemindersResponse>(`/api/reminders?tz=${encodeURIComponent(browserZone())}&lang=${lang}`, { signal: s }), [lang]);
  return (
    <>
      <PageHeader title={t('rem.title')} lead={t('rem.lead')} back={{ to: WEB_ROUTES.account, label: t('nav.account') }} />
      {data.status === 'loading' && <Loading />}
      {data.status === 'error' && <LoadError message={data.message} retry={data.reload} />}
      {data.status === 'ok' && <Body data={data.data} reload={data.reload} />}
    </>
  );
}

function Body({ data, reload }: { data: RemindersResponse; reload: () => void }) {
  const { t, rich } = useT();
  const student = data.contacts.filter(c => c.recipient === 'student');
  const wa = student.find(c => c.channel === 'whatsapp' && c.status !== 'stopped') ?? student.find(c => c.channel === 'whatsapp');
  const email = student.find(c => c.channel === 'email');
  const anyOn = student.some(c => c.status === 'active');
  const parents = data.contacts.filter(c => c.recipient === 'parent');
  return (
    <>
      <WhatsApp available={data.whatsapp_available} contact={wa} reload={reload} />
      <EmailSwitch contact={email} reload={reload} />
      <Kinds settings={data.settings} anyOn={anyOn} />
      {(data.whatsapp_available || parents.length > 0) && <Parents contacts={parents} reload={reload} />}
      <Recent entries={data.recent} />
      <p className="fine mt">{rich('rem.privacy', { privacy: <Link to={WEB_ROUTES.privacy}>{t('rem.privacyLink')}</Link> })}</p>
    </>
  );
}

function WhatsApp({ available, contact, reload }: { available: boolean; contact?: ReminderContact; reload: () => void }) {
  const { t, lang } = useT();
  const me = useMe();
  const handle = useErrorHandler();
  const [phone, setPhone] = useState('');
  const [consent, setConsent] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState<'' | 'send' | 'verify' | 'stop'>('');
  const [error, setError] = useState<{ phone?: string; consent?: string; code?: string; form?: string }>({});
  const [note, setNote] = useState('');
  const [another, setAnother] = useState(false);
  const focus = (id: string) => setTimeout(() => document.getElementById(id)?.focus(), 0);

  const fail = (e: unknown, field: 'phone' | 'code' | 'form') => {
    if (e instanceof ApiError) {
      const body = (e.body ?? {}) as { error?: string; reason?: string };
      if (body.error === 'reminders_unavailable') { setError({ form: t('rem.waOff') }); return; }
      if (e.code === 'invalid' && e.field === 'phone') {
        setAnother(true);
        setError({ phone: t(body.reason === 'not_on_whatsapp' ? 'rem.phoneNotWhatsApp' : body.reason === 'not_mobile' ? 'rem.phoneNotMobile' : 'rem.phoneInvalid') });
        focus('wa-phone');
        return;
      }
    }
    const msg = handle(e);
    if (msg) setError({ [field]: msg });
  };

  /* the number and the agreement typed on this page; "Send a new code" reuses them */
  const send = async (e?: FormEvent) => {
    e?.preventDefault();
    if (busy) return;
    if (!phone.trim()) { setAnother(true); setError({ phone: t('rem.phoneInvalid') }); focus('wa-phone'); return; }
    if (!consent) { setAnother(true); setError({ consent: t('rem.waConsentNeeded') }); focus('wa-consent'); return; }
    setBusy('send'); setError({}); setNote('');
    try {
      await api.post('/api/reminders/whatsapp', { phone, country: me.home_country ?? undefined, lang, tz: browserZone(), consent: true });
      setAnother(false); setCode('');
      reload();
      focus('wa-code');
    } catch (er) { fail(er, another || !contact || contact.status !== 'pending' ? 'phone' : 'form'); } finally { setBusy(''); }
  };

  const verify = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (code.length !== 6) { setError({ code: t('v.code') }); focus('wa-code'); return; }
    setBusy('verify'); setError({});
    try {
      const r = await api.post<{ contact: ReminderContact }>('/api/reminders/whatsapp/verify', { code });
      setNote(t('rem.waConfirmed', { phone: r.contact.phone ?? '' }));
      setCode(''); setConsent(false); setPhone('');
      reload();
    } catch (er) { setCode(''); fail(er, 'code'); } finally { setBusy(''); }
  };

  const stop = async () => {
    if (busy || !contact) return;
    setBusy('stop'); setError({});
    try {
      await api.post(`/api/reminders/${encodeURIComponent(contact.id)}/stop`);
      setNote(t('rem.waStopped'));
      reload();
    } catch (er) { fail(er, 'form'); } finally { setBusy(''); }
  };

  const stoppedNote = contact?.status === 'stopped' && !note ? (
    contact.stop_reason === 'undeliverable' ? <Alert tone="warn">{t('rem.waUnreachable', { phone: contact.phone ?? '' })}</Alert>
      : contact.stop_reason === 'number_changed' ? <Alert tone="info">{t('rem.waChanged', { phone: contact.phone ?? '' })}</Alert>
      : contact.stop_reason === 'whatsapp_reply' ? <Alert tone="info">{t('rem.waStoppedReply')}</Alert>
      : null
  ) : null;

  let body;
  if (!available) body = <Alert tone="info">{t('rem.waOff')}</Alert>;
  else if (contact?.status === 'active' && !another) {
    body = (
      <div className="card stack">
        <Row icon={<Icon.bell />} tone="green" title={t('rem.waOn', { phone: contact.phone ?? '' })} end={<Chip tone="green">{t('rem.on')}</Chip>} />
        {error.form && <Alert>{error.form}</Alert>}
        <div className="actions">
          <Button variant="gray" small onClick={() => { setAnother(true); setNote(''); focus('wa-phone'); }}>{t('rem.waOther')}</Button>
          <Button variant="danger" small busy={busy === 'stop'} onClick={() => void stop()}>{t('rem.waStop')}</Button>
        </div>
      </div>
    );
  } else if (contact?.status === 'pending' && !another) {
    body = (
      <form className="card stack" noValidate onSubmit={e => void verify(e)} aria-labelledby="whatsapp-title">
        <p>{t('rem.waSent', { phone: contact.phone ?? '' })}</p>
        <CodeField id="wa-code" label={t('rem.waCode')} value={code} onChange={v => { setCode(sixDigits(v)); setError({}); }} error={error.code} autoFocus />
        {error.form && <Alert>{error.form}</Alert>}
        <div className="actions">
          <Button type="submit" busy={busy === 'verify'}>{t('rem.waConfirm')}</Button>
          <Button variant="gray" small busy={busy === 'send'} onClick={() => (phone && consent ? void send() : (setAnother(true), focus('wa-phone')))}>{t('rem.waResend')}</Button>
          <Button variant="plain" small onClick={() => { setAnother(true); setError({}); focus('wa-phone'); }}>{t('rem.waOther')}</Button>
        </div>
      </form>
    );
  } else {
    body = (
      <>
        {stoppedNote && <div className="mb">{stoppedNote}</div>}
        <form className="card stack" noValidate onSubmit={e => void send(e)} aria-labelledby="whatsapp-title">
          <TextField id="wa-phone" label={t('rem.waPhone')} hint={t('rem.waPhoneHint')} value={phone} onChange={v => { setPhone(v); setError({}); }}
            error={error.phone} ltr type="tel" inputMode="tel" autoComplete="tel" />
          <Checkbox id="wa-consent" checked={consent} onChange={v => { setConsent(v); setError({}); }} label={t('rem.waConsent')} error={error.consent} required />
          {error.form && <Alert>{error.form}</Alert>}
          <div className="actions">
            <Button type="submit" busy={busy === 'send'}>{t('rem.waSend')}</Button>
            {another && contact && contact.status !== 'stopped' && <Button variant="plain" small onClick={() => { setAnother(false); setError({}); }}>{t('common.cancel')}</Button>}
          </div>
        </form>
      </>
    );
  }

  return (
    <Section title={t('rem.waTitle')} id="whatsapp">
      {note && <div className="mb"><Alert tone="success">{note}</Alert></div>}
      {body}
    </Section>
  );
}

/* parents who added their own WhatsApp number on the student's progress page: the student sees them and can stop them */
function Parents({ contacts, reload }: { contacts: ReminderContact[]; reload: () => void }) {
  const { t } = useT();
  const handle = useErrorHandler();
  const [busy, setBusy] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const stop = async (c: ReminderContact) => {
    if (busy) return;
    setBusy(c.id); setError(''); setNote('');
    try {
      await api.post(`/api/reminders/${encodeURIComponent(c.id)}/stop`);
      setNote(t('rem.parentStoppedDone'));
      reload();
    } catch (e) { const m = handle(e); if (m) setError(m); } finally { setBusy(''); }
  };
  const state = (c: ReminderContact) => (c.status === 'active' ? t('rem.on') : c.status === 'pending' ? t('rem.parentPending')
    : c.stop_reason === 'link_stopped' ? t('rem.parentLinkStopped') : c.stop_reason === 'whatsapp_reply' ? t('rem.waStoppedReply') : t('rem.parentStoppedDone'));
  return (
    <Section title={t('rem.parentsTitle')} id="parents" foot={t('rem.parentsLead')}>
      {note && <div className="mb"><Live><Alert tone="success">{note}</Alert></Live></div>}
      {error && <div className="mb"><Alert>{error}</Alert></div>}
      {contacts.length > 0 && (
        <ul className="group list list--icons">
          {contacts.map(c => (
            <li key={c.id}>
              <Row icon={<Icon.bell />} tone={c.status === 'active' ? 'green' : 'gray'}
                title={c.link_label ? t('rem.parentRow', { label: c.link_label }) : t('rem.parentRowNoLabel')}
                sub={<>{c.phone && <bdi dir="ltr">{c.phone}</bdi>}{c.phone ? ' · ' : ''}{state(c)}</>}
                end={c.status === 'stopped' ? undefined
                  : <Button variant="danger" small busy={busy === c.id} onClick={() => void stop(c)}>{t('rem.parentStop')}</Button>} />
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function EmailSwitch({ contact, reload }: { contact?: ReminderContact; reload: () => void }) {
  const { t, lang } = useT();
  const me = useMe();
  const handle = useErrorHandler();
  const [on, setOn] = useState(contact?.status === 'active');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const change = async (v: boolean) => {
    if (busy) return;
    setBusy(true); setMsg(null); setOn(v);
    try {
      await api.put('/api/reminders/email', { on: v, lang, tz: browserZone() });
      setMsg({ tone: 'success', text: t(v ? 'rem.emailOnDone' : 'rem.emailOffDone') });
      reload();
    } catch (e) {
      setOn(!v);
      const m = handle(e);
      if (m) setMsg({ tone: 'error', text: m });
    } finally { setBusy(false); }
  };
  return (
    <Section title={t('rem.emailTitle')} id="email">
      <div className="card stack">
        <Checkbox id="email-on" checked={on} onChange={v => void change(v)} label={t('rem.emailLabel', { email: me.user.email })} />
        <Live>{msg && <Alert tone={msg.tone}>{msg.text}</Alert>}</Live>
      </div>
    </Section>
  );
}

function Kinds({ settings, anyOn }: { settings: ReminderSettings; anyOn: boolean }) {
  const { t } = useT();
  const handle = useErrorHandler();
  const [s, setS] = useState(settings);
  const [saved, setSaved] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [testBusy, setTestBusy] = useState(false);
  const zone = browserZone();
  const change = async (key: keyof ReminderSettings, v: boolean) => {
    const next = { ...s, [key]: v };
    setS(next); setSaved(''); setError(null);
    try { await api.put('/api/reminders/settings', next); setSaved(t('rem.saved')); }
    catch (e) { setS(s); const m = handle(e); if (m) setError(m); }
  };
  const test = async () => {
    if (testBusy) return;
    setTestBusy(true); setSaved(''); setError(null);
    try { await api.post('/api/reminders/test'); setSaved(t('rem.testSent')); }
    catch (e) { const m = handle(e); if (m) setError(m); }
    finally { setTestBusy(false); }
  };
  return (
    <Section title={t('rem.kindsTitle')} id="kinds">
      <div className="card stack">
        {KINDS.map(k => (
          <Checkbox key={k.key} id={`kind-${k.key}`} checked={s[k.key]} onChange={v => void change(k.key, v)} label={t(k.title)} description={t(k.desc)} />
        ))}
        <p className="fine">{t('rem.when', { tz: zone })}</p>
        {anyOn && <div className="actions"><Button variant="secondary" small busy={testBusy} onClick={() => void test()}><Icon.bell />{t('rem.test')}</Button></div>}
        <Live>{error ? <Alert>{error}</Alert> : saved ? <span className="fine">{saved}</span> : null}</Live>
      </div>
    </Section>
  );
}

function Recent({ entries }: { entries: RemindersResponse['recent'] }) {
  const { t, lang } = useT();
  return (
    <Section title={t('rem.recentTitle')} id="recent">
      {entries.length === 0 ? <p className="fine">{t('rem.recentNone')}</p> : (
        <ul className="group list">
          {entries.map(e => (
            <li key={e.id}>
              <Row icon={e.channel === 'whatsapp' ? <Icon.bell /> : <Icon.mail />} tone={e.status === 'failed' ? 'red' : 'gray'}
                title={t(`rem.kind.${e.kind as Exclude<ReminderKind, 'verify'>}` as MessageKey)}
                sub={`${t(`rem.channel.${e.channel}` as MessageKey)} · ${formatDateTime(e.created_at, lang)}${e.due_date ? ` · ${formatDate(e.due_date, lang)}` : ''}`}
                end={<Chip tone={e.status === 'failed' ? 'red' : e.status === 'read' || e.status === 'delivered' ? 'green' : 'gray'}>{t(`rem.status.${e.status}` as MessageKey)}</Chip>} />
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}
